// 📄 src/utils/demoDataGenerator.js

// Demo data generator for Intelligence Navigator
export class DemoDataGenerator {
  constructor() {
    this.companies = [
      'TechFlow Solutions', 'DataVision Inc', 'CloudScale Labs', 'AI Dynamics',
      'MetricPro Systems', 'InsightEdge Corp', 'SmartAnalytics Co', 'DataForge Ltd'
    ];
    
    this.industries = [
      'Technology', 'Healthcare', 'Finance', 'E-commerce', 'Manufacturing', 
      'Education', 'Real Estate', 'Marketing'
    ];

    this.crmTypes = ['SALESFORCE', 'HUBSPOT', 'PIPEDRIVE', 'ZOHO'];
    
    this.leadSources = [
      'Website', 'Social Media', 'Email Campaign', 'Referral', 'Cold Call',
      'Trade Show', 'Content Marketing', 'Paid Ads', 'Webinar', 'Partner'
    ];

    this.dealStages = [
      'Prospecting', 'Qualification', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'
    ];

    this.customerSegments = [
      'Enterprise', 'Mid-Market', 'Small Business', 'Startup', 'Government', 'Non-Profit'
    ];
  }

  // Generate random number within range
  random(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  // Generate random float within range
  randomFloat(min, max, decimals = 2) {
    return parseFloat((Math.random() * (max - min) + min).toFixed(decimals));
  }

  // Generate random date within last N days
  randomDate(daysBack = 365) {
    const start = new Date();
    start.setDate(start.getDate() - daysBack);
    const end = new Date();
    return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
  }

  // Generate demo client
  generateDemoClient() {
    return {
      id: 'demo-client-001',
      name: 'Demo User',
      email: 'demo@intelligencenavigator.com',
      company: this.companies[this.random(0, this.companies.length - 1)],
      industry: this.industries[this.random(0, this.industries.length - 1)],
      createdAt: this.randomDate(90),
      plan: 'Professional',
      status: 'active'
    };
  }

  // Generate CRM configurations
  generateCRMConfigs(count = 2) {
    const configs = [];
    
    for (let i = 0; i < count; i++) {
      const crmType = this.crmTypes[i % this.crmTypes.length];
      configs.push({
        id: `demo-crm-${i + 1}`,
        crmType,
        isActive: i === 0 || Math.random() > 0.3, // First one always active
        lastSync: this.randomDate(7),
        createdAt: this.randomDate(30),
        syncStatus: this.random(1, 10) > 2 ? 'healthy' : 'warning'
      });
    }
    
    return configs;
  }

  // Generate revenue data
  generateRevenueData(months = 12) {
    const data = [];
    const baseRevenue = 450000; // Base monthly revenue
    let currentRevenue = baseRevenue;
    
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    for (let i = 0; i < months; i++) {
      // Add growth trend with some seasonality
      const seasonalMultiplier = 1 + (Math.sin((i * Math.PI) / 6) * 0.1);
      const growthRate = this.randomFloat(0.95, 1.15); // -5% to +15% month over month
      
      currentRevenue = currentRevenue * growthRate * seasonalMultiplier;
      
      data.push({
        month: monthNames[i % 12],
        revenue: Math.round(currentRevenue / 1000), // In thousands
        customers: this.random(40, 85),
        conversion: this.randomFloat(2.8, 4.5),
        profit: Math.round((currentRevenue * 0.3) / 1000), // 30% profit margin
        deals: this.random(15, 35),
        leads: this.random(200, 450)
      });
    }
    
    return data;
  }

  // Generate customer segments
  generateCustomerSegments() {
    const segments = [];
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
    
    this.customerSegments.forEach((segment, index) => {
      segments.push({
        name: segment,
        value: this.random(15, 35),
        color: colors[index % colors.length],
        revenue: this.random(50000, 300000),
        growth: this.randomFloat(-5, 25),
        customers: this.random(20, 150)
      });
    });
    
    return segments;
  }

  // Generate sales opportunities
  generateOpportunities(count = 50) {
    const opportunities = [];
    
    for (let i = 0; i < count; i++) {
      const stage = this.dealStages[this.random(0, this.dealStages.length - 1)];
      const amount = this.random(5000, 150000);
      const probability = stage === 'Closed Won' ? 100 : 
                        stage === 'Closed Lost' ? 0 :
                        this.random(20, 85);
      
      opportunities.push({
        id: `demo-opp-${i + 1}`,
        name: `${this.companies[this.random(0, this.companies.length - 1)]} - ${stage}`,
        amount,
        stage,
        probability,
        closeDate: this.randomDate(90),
        createdDate: this.randomDate(180),
        source: this.leadSources[this.random(0, this.leadSources.length - 1)],
        segment: this.customerSegments[this.random(0, this.customerSegments.length - 1)]
      });
    }
    
    return opportunities;
  }

  // Generate accounts/companies
  generateAccounts(count = 30) {
    const accounts = [];
    
    for (let i = 0; i < count; i++) {
      accounts.push({
        id: `demo-account-${i + 1}`,
        name: this.companies[this.random(0, this.companies.length - 1)],
        industry: this.industries[this.random(0, this.industries.length - 1)],
        annualRevenue: this.random(100000, 10000000),
        employees: this.random(10, 5000),
        segment: this.customerSegments[this.random(0, this.customerSegments.length - 1)],
        status: this.random(1, 10) > 2 ? 'active' : 'inactive',
        createdDate: this.randomDate(365),
        lastActivity: this.randomDate(30)
      });
    }
    
    return accounts;
  }

  // Generate leads
  generateLeads(count = 100) {
    const leads = [];
    const statuses = ['New', 'Contacted', 'Qualified', 'Unqualified', 'Converted'];
    
    for (let i = 0; i < count; i++) {
      const status = statuses[this.random(0, statuses.length - 1)];
      
      leads.push({
        id: `demo-lead-${i + 1}`,
        name: `Lead ${i + 1}`,
        company: this.companies[this.random(0, this.companies.length - 1)],
        email: `lead${i + 1}@${this.companies[this.random(0, this.companies.length - 1)].toLowerCase().replace(/\s+/g, '')}.com`,
        status,
        source: this.leadSources[this.random(0, this.leadSources.length - 1)],
        score: this.random(1, 100),
        createdDate: this.randomDate(90),
        convertedDate: status === 'Converted' ? this.randomDate(30) : null
      });
    }
    
    return leads;
  }

  // Generate AI insights
  generateInsights(count = 15) {
    const insights = [];
    const types = ['TREND_ANALYSIS', 'RECOMMENDATION', 'ANOMALY_DETECTION', 'FORECASTING', 'CUSTOMER_SEGMENTATION'];
    
    const insightTemplates = {
      TREND_ANALYSIS: [
        {
          title: 'Revenue Growth Acceleration',
          description: 'Revenue has increased by 15% over the past quarter, with Enterprise segment showing strongest growth.',
          points: [
            'Enterprise deals increased by 23% in Q3',
            'Average deal size grew from $45K to $52K',
            'Sales cycle reduced by 12 days on average'
          ]
        },
        {
          title: 'Customer Acquisition Trends',
          description: 'New customer acquisition is trending upward with improved conversion rates.',
          points: [
            'Conversion rate improved from 3.2% to 4.1%',
            'Lead quality increased across all channels',
            'Customer lifetime value up 18%'
          ]
        }
      ],
      RECOMMENDATION: [
        {
          title: 'Optimize Marketing Spend',
          description: 'Reallocate budget from low-performing channels to high-converting sources.',
          points: [
            'Social Media campaigns show 45% higher ROI',
            'Reduce spend on Trade Shows by 30%',
            'Increase content marketing investment'
          ]
        },
        {
          title: 'Focus on Mid-Market Segment',
          description: 'Mid-market customers show highest retention and growth potential.',
          points: [
            'Mid-market churn rate is 40% lower than average',
            'Expansion revenue potential of $2.3M identified',
            'Shorter sales cycles in this segment'
          ]
        }
      ],
      ANOMALY_DETECTION: [
        {
          title: 'Churn Risk Alert',
          description: 'Detected increased churn risk in the Small Business segment.',
          points: [
            '23% increase in support tickets',
            'Usage metrics declining over 30 days',
            'Renewal rate dropped to 78%'
          ]
        }
      ],
      FORECASTING: [
        {
          title: 'Q4 Revenue Forecast',
          description: 'Based on current trends, Q4 revenue is projected to reach $3.2M.',
          points: [
            '95% confidence interval: $2.9M - $3.5M',
            'Assumes current sales velocity continues',
            'Risk factors: holiday seasonality, economic conditions'
          ]
        }
      ],
      CUSTOMER_SEGMENTATION: [
        {
          title: 'High-Value Customer Profile',
          description: 'Identified characteristics of highest-value customer segment.',
          points: [
            'Technology companies with 100-500 employees',
            'Annual revenue between $10M-$50M',
            'Multiple decision makers involved'
          ]
        }
      ]
    };

    for (let i = 0; i < count; i++) {
      const type = types[this.random(0, types.length - 1)];
      const templates = insightTemplates[type];
      const template = templates[this.random(0, templates.length - 1)];
      
      insights.push({
        id: `demo-insight-${i + 1}`,
        type,
        title: template.title,
        description: template.description,
        points: template.points,
        confidence: this.randomFloat(0.7, 0.95),
        dataSource: ['REVENUE', 'CUSTOMERS', 'SALES'][this.random(0, 2)],
        createdAt: this.randomDate(30),
        isActive: true
      });
    }
    
    return insights;
  }

  // Generate KPI data
  generateKPIData() {
    const current = {
      revenue: 1250000,
      customers: 345,
      conversion: 3.8,
      churn: 8.5,
      ltv: 45000,
      cac: 1200
    };

    const previous = {
      revenue: 1080000,
      customers: 318,
      conversion: 3.5,
      churn: 9.2,
      ltv: 42000,
      cac: 1350
    };

    const calculateGrowth = (curr, prev) => {
      return ((curr - prev) / prev * 100).toFixed(1);
    };

    return {
      revenue: {
        current: current.revenue,
        previous: previous.revenue,
        growth: calculateGrowth(current.revenue, previous.revenue),
        trend: 'up'
      },
      customers: {
        current: current.customers,
        previous: previous.customers,
        growth: calculateGrowth(current.customers, previous.customers),
        trend: 'up'
      },
      conversion: {
        current: current.conversion,
        previous: previous.conversion,
        growth: calculateGrowth(current.conversion, previous.conversion),
        trend: 'up'
      },
      churn: {
        current: current.churn,
        previous: previous.churn,
        growth: calculateGrowth(current.churn, previous.churn),
        trend: 'down' // Lower churn is better
      }
    };
  }

  // Generate complete demo dashboard data
  generateCompleteDashboard() {
    return {
      client: this.generateDemoClient(),
      crmConfigs: this.generateCRMConfigs(),
      kpis: this.generateKPIData(),
      revenueData: this.generateRevenueData(),
      customerSegments: this.generateCustomerSegments(),
      opportunities: this.generateOpportunities(),
      accounts: this.generateAccounts(),
      leads: this.generateLeads(),
      insights: this.generateInsights(),
      dataQuality: {
        score: this.random(75, 95),
        level: 'good',
        dataPoints: this.random(500, 1200),
        insights: this.random(8, 15)
      },
      lastUpdated: new Date()
    };
  }

  // Generate time-series data for sparklines
  generateSparklineData(points = 10, trend = 'up') {
    const data = [];
    let value = this.random(20, 80);
    
    for (let i = 0; i < points; i++) {
      if (trend === 'up') {
        value += this.randomFloat(-5, 10);
      } else if (trend === 'down') {
        value += this.randomFloat(-10, 5);
      } else {
        value += this.randomFloat(-8, 8);
      }
      
      data.push(Math.max(0, Math.min(100, value)));
    }
    
    return data;
  }
}

// Export singleton instance
export const demoDataGenerator = new DemoDataGenerator();

// Export utility functions
export const generateDemoToken = () => {
  return 'demo-jwt-token-' + Math.random().toString(36).substr(2, 9);
};

export const isDemoMode = () => {
  return process.env.REACT_APP_DEMO_MODE === 'true' || 
         localStorage.getItem('demo_mode') === 'true';
};

export const enableDemoMode = () => {
  localStorage.setItem('demo_mode', 'true');
  localStorage.setItem('auth_token', generateDemoToken());
  localStorage.setItem('user_data', JSON.stringify(demoDataGenerator.generateDemoClient()));
};

export const disableDemoMode = () => {
  localStorage.removeItem('demo_mode');
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_data');
};

export default demoDataGenerator;
